import { useConfigStore } from '@/store/useConfigStore';
import { InputField } from '@/components/ui/InputField';
import { StepperInput } from '@/components/ui/StepperInput';
import { SelectionCard } from '@/components/ui/SelectionCard';
import { Columns3, ArrowUpFromLine, Box, MoveDown, Grid3X3, MinusSquare } from 'lucide-react';
import type { WindowConfig } from '@/types/curtain';

export function Step1RoomWindow() {
  const { windowConfig, updateWindowConfig } = useConfigStore();

  const handleUpdate = (key: keyof WindowConfig, value: any) => {
    updateWindowConfig({ [key]: value });
  };

  const mountType = windowConfig.mountType || 'wall';
  const dropStyle = windowConfig.dropStyle || 'floating';

  return (
    <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-300">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Room & Window Details</h2>
          <p className="text-slate-600">Enter the basic measurements and mounting preferences for your curtain.</p>
        </div>
        <button
          onClick={() => useConfigStore.getState().setVisualizerOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 rounded-lg font-medium transition-colors border border-indigo-200"
        >
          <span>👓 3D Room Visualizer</span>
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <InputField 
          label="Room Name" 
          placeholder="e.g. Master Bedroom" 
          value={windowConfig.roomName || ''}
          onChange={(e) => handleUpdate('roomName', e.target.value)}
        />
        <InputField 
          label="Window Name" 
          placeholder="e.g. East Facing" 
          value={windowConfig.windowName || ''}
          onChange={(e) => handleUpdate('windowName', e.target.value)}
        />
      </div>

      <div className="space-y-4">
        <label className="text-sm font-medium text-slate-700 block">Mount Type</label>
        <div className="grid grid-cols-2 gap-4">
          <SelectionCard
            title="Wall Mount"
            description="Mounted above the window frame on the wall"
            icon={Columns3}
            selected={mountType === 'wall'}
            onClick={() => handleUpdate('mountType', 'wall')}
          />
          <SelectionCard
            title="Ceiling Mount"
            description="Mounted directly to the ceiling"
            icon={ArrowUpFromLine}
            selected={mountType === 'ceiling'}
            onClick={() => handleUpdate('mountType', 'ceiling')}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StepperInput
          label="Track Width"
          value={windowConfig.trackWidth || 2000}
          onChange={(val) => handleUpdate('trackWidth', val)}
          unit="mm"
          min={500}
          max={10000}
        />
        <StepperInput
          label="Finished Drop"
          value={windowConfig.finishedDrop || 2500}
          onChange={(val) => handleUpdate('finishedDrop', val)}
          unit="mm"
          min={500}
          max={5000}
        />
      </div>

      <div className="space-y-4">
        <label className="text-sm font-medium text-slate-700 block">Drop Style</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <SelectionCard
            title="Floating"
            description="Sits just above the floor"
            icon={MinusSquare}
            selected={dropStyle === 'floating'}
            onClick={() => handleUpdate('dropStyle', 'floating')}
          />
          <SelectionCard
            title="Touching"
            description="Barely touches the floor"
            icon={MoveDown}
            selected={dropStyle === 'touching'}
            onClick={() => handleUpdate('dropStyle', 'touching')}
          />
          <SelectionCard
            title="Puddle"
            description="Pools on the floor"
            icon={Box}
            selected={dropStyle === 'puddle'}
            onClick={() => handleUpdate('dropStyle', 'puddle')}
          />
          <SelectionCard
            title="Sill"
            description="Ends at the window sill"
            icon={Grid3X3}
            selected={dropStyle === 'sill-length'}
            onClick={() => handleUpdate('dropStyle', 'sill-length')}
          />
        </div>
      </div>

      {dropStyle === 'floating' && (
        <div className="space-y-4 pt-4 border-t border-slate-100">
          <div className="flex justify-between">
            <label className="text-sm font-medium text-slate-700">Floor Clearance</label>
            <span className="text-sm text-slate-500 font-medium">{windowConfig.floorClearance || 15} mm</span>
          </div>
          <input 
            type="range" 
            min="5" 
            max="50" 
            step="5"
            value={windowConfig.floorClearance || 15}
            onChange={(e) => handleUpdate('floorClearance', parseInt(e.target.value))}
            className="w-full accent-amber-500"
          />
          <div className="flex justify-between text-xs text-slate-400">
            <span>5mm</span>
            <span>50mm</span>
          </div>
        </div>
      )}
    </div>
  );
}
